// Inspired here and slightly changed to better fit our use case.
// https://levelup.gitconnected.com/useonlinestatus-a-react-hook-to-know-when-your-app-is-offline-2d06e4e536a

import React, { useState, useEffect, useContext } from 'react'
import { isIE } from 'shared/helpers'

const TIMEOUT_TIME_MS = 3000
const ONLINE_POLL_INTERVAL = 10000

const timeout = (time: number, promise: Promise<Response>) => {
  return new Promise(function (resolve, reject) {
    setTimeout(() => {
      reject(new Error('Request timed out.'))
    }, time)
    promise.then(resolve, reject)
  })
}

const checkOnlineStatus = async () => {
  // IE does not have AbortController
  if (isIE) {
    return navigator.onLine
  }
  const controller = new AbortController()
  const { signal } = controller

  // If the browser has no network connection return offline
  if (!navigator.onLine) return navigator.onLine

  try {
    await timeout(
      TIMEOUT_TIME_MS,
      fetch('', {
        method: 'GET',
        signal
      })
    )
    return true
  } catch (error) {
    // This can be because of request timed out
    // so we abort the request for any case
    controller.abort()
  }
  return false
}

const OnlineStatusContext = React.createContext(true)

export const OnlineStatusProvider: React.FC = ({ children }) => {
  const [onlineStatus, setOnlineStatus] = useState<boolean>(true)
  const [intervalId, setIntervalId] = useState<number | null>(null)

  const checkStatus = async () => {
    const online = await checkOnlineStatus()
    setOnlineStatus(online)
  }

  const onOffline = () => {
    setOnlineStatus(false)

    setIntervalId(
      window.setInterval(() => {
        checkStatus()
      }, ONLINE_POLL_INTERVAL)
    )
  }

  const onOnline = () => {
    checkStatus()
  }

  useEffect(() => {
    // If onlineStatus changed to true, we have to call clearInterval to avoid unnecessary
    // bombarding our backend servers.
    if (onlineStatus && intervalId) {
      clearInterval(intervalId)
      setIntervalId(null)
    }
  }, [onlineStatus])

  useEffect(() => {
    // Online listener is not necessary to have as polled checkStatus can find out working connection too.
    // This is here only to execute checkStatus immediately when online event was fired to notify all components about
    // working internet connection ASAP.
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)

    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)

      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [])

  return (
    <OnlineStatusContext.Provider value={onlineStatus}>
      {children}
    </OnlineStatusContext.Provider>
  )
}

export const useOnlineStatus = () => {
  const store = useContext(OnlineStatusContext)
  return store
}

export default useOnlineStatus
